import { ApiError } from '@micarahr/api-client';
import type { UserRole } from '@micarahr/shared-types';
import { WEB_SUPPORTED_ROLES, primaryWebRole } from '@micarahr/permissions/web';
import { apiSession } from './api-client';
import { IDLE_SIGN_OUT_NOTICE, leaveSignOutNotice } from '../lib/sign-out-notice';
import type { Session } from './auth.mock';
import { DEMO_CREDENTIALS, DEMO_PASSWORD } from './demo-credentials';

/**
 * Authentication against the real API.
 *
 * Implements the same `authService` surface the prototype did, so no screen
 * changes: the sign-in form, the shell and the command bar all keep calling
 * `signIn`, `restore` and `signOut` and never learn which one answered.
 *
 * Two differences are real and are handled here rather than pushed upward.
 *
 * `restore()` cannot be synchronous any more. The prototype read a session out
 * of `localStorage`; restoring a real session means presenting a refresh cookie
 * to the server and waiting. The shell already awaits bootstrap, so it awaits
 * this too.
 *
 * Permissions come from the server, not from the role. The prototype derived
 * them locally from a role table; the server sends the actual grants, which may
 * differ — a role whose permissions were edited is exactly the case where a
 * locally derived list is wrong and confidently so.
 */

// One source of truth for the demonstration accounts; the list lives in a
// module small enough to ship and is only ever rendered behind dev-only gates.

interface ApiPrincipal {
  id: string;
  email: string;
  displayName: string;
  employeeId: string | null;
  roles: string[];
  permissions: string[];
}

/**
 * Turn the server's principal into the frontend's session model.
 *
 * The mapping exists so the API's shape stays out of the UI: a screen asking
 * `session.scopedDepartmentIds` should not have to know the server calls them
 * scopes and returns them as `{kind, targetId}`.
 */
function toSession(principal: ApiPrincipal, scopes: { kind: string; targetId: string }[]): Session {
  const roles = principal.roles.filter((role): role is UserRole =>
    (WEB_SUPPORTED_ROLES as readonly string[]).includes(role),
  );

  return {
    userId: principal.id,
    employeeId: principal.employeeId,
    displayName: principal.displayName,
    email: principal.email,
    roles,
    primaryRole: primaryWebRole(roles),
    scopedDepartmentIds: scopes
      .filter((scope) => scope.kind === 'department')
      .map((scope) => scope.targetId),
    // The server's grants, not a locally derived guess. A role whose
    // permissions were edited is precisely where the guess is wrong.
    permissions: principal.permissions,
    signedInAt: new Date().toISOString(),
  };
}

async function loadSession(): Promise<Session> {
  const me = await apiSession().transport.get<{
    user: ApiPrincipal;
    roles: string[];
    permissions: string[];
    scopes: { kind: string; targetId: string }[];
  }>('/api/v1/auth/me');

  return toSession({ ...me.user, roles: me.roles, permissions: me.permissions }, me.scopes ?? []);
}

async function consoleSessionOrSignOut(): Promise<Session> {
  const session = await loadSession();

  if (session.roles.length === 0) {
    // An employee-only account reaching the HR web app. The mobile app is
    // where their own records live.
    await apiSession().signOut();
    throw new Error(
      'This account has employee access only. Use the MicaraHR mobile app to view your own records.',
    );
  }

  return session;
}

/**
 * The words for a passwordless sign-in that did not finish, by the server's
 * code. The same table reads the `?error=` a Google sign-in comes back with.
 */
const SIGN_IN_ERRORS: Record<string, string> = {
  SIGN_IN_LINK_INVALID:
    'That sign-in link has expired or has already been used. Ask for a new one.',
  ACCOUNT_INACTIVE: 'This account is not active. Ask your administrator to restore access.',
  WEB_CONSOLE_NOT_ALLOWED:
    'This account has employee access only. Use the MicaraHR mobile app to view your own records.',
  GOOGLE_ACCOUNT_NOT_REGISTERED:
    "This Google account isn't registered in MicaraHR. Ask your administrator.",
  GOOGLE_CANCELLED: 'Google sign-in was cancelled.',
  GOOGLE_SIGN_IN_FAILED: "Google sign-in didn't complete. Try again.",
  GOOGLE_SIGN_IN_UNAVAILABLE:
    'Google sign-in is not available. Sign in with an emailed link instead.',
  PASSWORD_LOGIN_DISABLED: 'Password sign-in is turned off. Sign in with an emailed link instead.',
};

export function signInErrorText(code: string): string {
  return SIGN_IN_ERRORS[code] ?? 'Sign-in did not complete. Try again.';
}

function signInErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return SIGN_IN_ERRORS[error.code] ?? error.serverMessage ?? 'Sign-in did not complete.';
  }
  return error instanceof Error ? error.message : 'Sign-in did not complete.';
}

export const apiAuthService = {
  /**
   * Restore from the refresh cookie.
   *
   * Returns null for every ordinary reason a session is not restorable —
   * never signed in, expired over a weekend, revoked by an administrator. None
   * of those is an error worth surfacing at boot.
   */
  async restore(): Promise<Session | null> {
    const restored = await apiSession().restore();
    if (!restored) {
      // A console reopened after the idle timeout: say so on the sign-in form
      // rather than presenting it as if nothing had happened.
      const state = apiSession().getState();
      if (state.status === 'signed_out' && state.reason === 'idle_timeout') {
        leaveSignOutNotice(state.message ?? IDLE_SIGN_OUT_NOTICE);
      }
      return null;
    }

    try {
      return await loadSession();
    } catch {
      return null;
    }
  },

  async signIn(email: string, password: string): Promise<Session> {
    try {
      await apiSession().signIn(email.trim().toLowerCase(), password);
    } catch (error: unknown) {
      // The server is deliberately vague about which half was wrong, and that
      // vagueness must survive: an error distinguishing them is an
      // account-enumeration oracle.
      if (error instanceof ApiError && error.code === 'PASSWORD_LOGIN_DISABLED') {
        throw new Error(signInErrorText(error.code));
      }
      if (error instanceof ApiError && error.category === 'authentication') {
        throw new Error('That email address and password do not match an account.');
      }
      throw error;
    }

    return consoleSessionOrSignOut();
  },

  async signInOptions() {
    return apiSession().signInOptions();
  },

  async requestSignInLink(email: string): Promise<string> {
    const result = await apiSession().requestSignInLink(email.trim().toLowerCase());
    return result.message;
  },

  /**
   * Finish an emailed-link sign-in. The server checks the console roles too;
   * the check after it is the same belt-and-braces as for a password.
   */
  async signInWithLink(token: string): Promise<Session> {
    try {
      await apiSession().signInWithLink(token);
    } catch (error: unknown) {
      throw new Error(signInErrorMessage(error));
    }
    return consoleSessionOrSignOut();
  },

  googleSignInUrl(): string {
    return apiSession().googleSignInUrl();
  },

  async signOut(): Promise<void> {
    await apiSession().signOut();
  },

  /**
   * Switch demonstration account.
   *
   * A real sign-in against the seeded credentials, not an impersonation: the
   * server issues a session for that account and every permission check that
   * follows is the real one.
   */
  async switchTo(email: string): Promise<Session> {
    // Defence in depth behind the UI gate. Static `import.meta.env.PROD` so
    // the bundler removes the unreachable branch — the seeded password is
    // absent from a production bundle, not merely unused.
    if (import.meta.env.PROD) {
      throw new Error('Demonstration account switching is disabled in production.');
    }
    await apiSession().signOut();
    return apiAuthService.signIn(email, DEMO_PASSWORD);
  },

  demoAccounts() {
    return DEMO_CREDENTIALS;
  },
};
